import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  IResourceClient,
  ResourceClientQuery,
} from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  selectPermissionResource,
  selectPermissionResourceDetail,
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  deleteResource,
  ErrorMessage,
  fetchResources,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  SearchboxEmit,
  SearchboxOptions,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import { AdvancedSearchCriteria } from '../components/AdvancedSearch';

type Arguments = {
  callback?: () => void;
  disableQuery?: boolean;
};

const useResource2 = (options?: Arguments) => {
  const callback = options?.callback;
  const disableQuery = options?.disableQuery;
  const enableQuery = !disableQuery;
  const resource = useResource();
  const { clientId } = resource;
  const defaultParams = {
    first: CONFIG.pagination.offset,
    max: CONFIG.pagination.step,
    name: '',
    type: '',
  };
  const defaultData = {
    items: [],
    totalRecord: 0,
  };
  const defaultSelection = {
    item: null,
  };
  const queryClient = useQueryClient();
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const { canViewPage: canEdit } = useSelector(selectPermissionResourceDetail);
  const { canViewAdd: canAdd, canViewDelete: canDelete } = useSelector(
    selectPermissionResource
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [params, setParams] = useState<ResourceClientQuery>({
    ...defaultParams,
  });
  const [selectedItem, setSelectedItem] = useState<IResourceClient | null>(
    defaultSelection.item
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const isFilter = params.type !== '';
  const queryKeys = [
    QUERY_KEY.RESOURCE_LIST,
    resource.clientId,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(queryKeys, () => fetchResources(clientId, params), {
    enabled: enableQuery,
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot get resources';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      if ((isSearch && isSearching) || (isFilter && isSearching)) {
        setIsSearching(false);
      }

      if (wasSearch && !isSearch && !isFilter) {
        setWasSearch(false);
      }

      if (!isSearch && !isFilter) {
        setPreviousOffset(params.first);
      }
    },
    staleTime: CONFIG.query.staleTime,
  });

  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const deleteMutation = useMutation(deleteResource, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot delete resource';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      setIsDeleteDialogOpen(false);
      setNotification({
        message: 'Resource deleted',
        type: 'success',
      });

      if (enableQuery) {
        handleSelectionReset();
        handleReload();
      }

      callback && callback();
    },
  });

  const handleSelectionReset = () => {
    setSelectedItem(defaultSelection.item);
  };

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleReload = () => {
    queryClient.invalidateQueries(queryKeys);
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleCreateRequest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleDeleteRequest = (data: IResourceClient) => {
    if (data) {
      setSelectedItem(data);
      setIsDeleteDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    handleSelectionReset();
  };

  const handleCreateAction = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
    handleReload();
  };

  const handleDeleteAction = () => {
    if (selectedItem) {
      deleteMutation.mutate({
        clientId: resource.clientId,
        resourceId: selectedItem._id,
      });
    }
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged || isFilter);
    setParams((previous) => ({
      ...previous,
      first: offset,
      name: value,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword && !isFilter);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const hasKeyword = isSearch;
    const offset = previousOffset;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...defaultParams,
      first: offset,
      max: previous.max,
    }));

    if (hasKeyword) {
      setWasSearch(true);
      setSearchQuery(value);
    } else {
      handleRefetch();
    }
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      if (isFilter) {
        setIsSearching(true);
        setWasSearch(false);
        setParams((previous) => ({
          ...previous,
          name: value,
        }));
        setSearchQuery(value);
      } else {
        handleSearchReset();
      }
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handleAdvancedSearchChange = (criteria: AdvancedSearchCriteria) => {
    const { type } = criteria;
    const hasCriteria = type !== '';
    const offset = hasCriteria ? 0 : previousOffset;

    setIsSearching(hasCriteria);
    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      ...criteria,
      first: offset,
    }));
    handleRefetch();
  };

  const handlePageChange = (offset: number) => {
    if (wasSearch) {
      setWasSearch(false);
    }

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPreviousOffset(offset);
    setPageStep(step);
    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));
    handleRefetch();
  };

  return {
    canAdd,
    canDelete,
    canEdit,
    data,
    handleAdvancedSearchChange,
    handleCreateAction,
    handleCreateCancel,
    handleCreateRequest,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
    handlePageChange,
    handleRefresh,
    handleSearchChange,
    handleSearchClear,
    handleSearchEnter,
    handleSearchReset,
    handleStepChange,
    isCreateDialogOpen,
    isDeleteDialogOpen,
    isDeleting: deleteMutation.isLoading,
    isFetched,
    isFetching,
    isFilter,
    isLoading,
    isProcessing,
    isSearch,
    isSearching,
    paginationOffset: currentOffset,
    paginationStep: pageStep,
    params,
    searchQuery,
    selectedItem,
    wasSearch,
  };
};

export { useResource2 };
